/*
  theme 92.0 (section 15, the header) — the search drawer's typed opening, copied from the source.

  ⚠️ It is NOT a rotating placeholder, and it is not on the input at all. Sampling
  input[placeholder] over nine seconds returns ONE static string on both sides
  (review-hd-92/search-92.json) — reading it there is how this sat unnoticed. The moving text is
  its own element with an inserted blinking cursor, painted over the native placeholder.

  The source (search.js, the live bundle), read and then confirmed against samples of its own
  rendered text at 1440 and 390 (review-hd-92/typed-92.json):
    insert a "|" cursor span after this element, inside the parent
    type data-first-text  — width 0 -> scrollWidth over 1s, after data-delay (0.5s) — then blink
    wait 600ms
    erase — width -> 0 over 0.25s, cursor stops blinking
    type data-last-text over 1s, no delay, then blink
    hide the PARENT on the input's pointerdown or first keystroke
  Two texts, once, on open. Not a loop.

  data-last-text is the input's own placeholder, which is what makes the hand-off invisible: when
  the parent hides, the native placeholder underneath is already the same words.
*/

const TYPE_DURATION = 1000;
const ERASE_DURATION = 250;
const PAUSE_BETWEEN = 600;

class SearchTyped extends HTMLElement {
  /** @type {HTMLElement | null} */
  #cursor = null;

  /** @type {boolean} */
  #started = false;

  /** @type {number[]} */
  #timers = [];

  /** @type {Animation | null} The one width animation in flight, cancelled before the next starts. */
  #anim = null;

  connectedCallback() {
    const input = this.#input;
    if (!input) return;

    input.addEventListener('pointerdown', this.#hide);
    input.addEventListener('input', this.#hide);

    // The source starts on inView. The drawer is off-screen until it opens, so the equivalent
    // here is the first time this element actually has a box.
    if (typeof IntersectionObserver === 'function') {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) this.#start();
      });
      this.observer.observe(this);
    } else {
      this.#start();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.#input?.removeEventListener('pointerdown', this.#hide);
    this.#input?.removeEventListener('input', this.#hide);
    this.#timers.forEach(clearTimeout);
    this.#timers = [];
  }

  /** @returns {HTMLInputElement | null} The search field this sits over. */
  get #input() {
    return this.closest('form')?.querySelector('input[type="search"]') ?? null;
  }

  /** @returns {number} The source's data-delay, in milliseconds. */
  get #startDelay() {
    return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) * 1000 : 0;
  }

  /**
   * Once the customer touches the field the whole thing goes, parent included — the source sets
   * `hidden` on the PARENT, not on itself, so the cursor it inserted there goes with it.
   */
  #hide = () => {
    this.#timers.forEach(clearTimeout);
    this.#timers = [];
    const parent = this.parentElement;
    if (parent && !parent.hasAttribute('hidden')) parent.setAttribute('hidden', '');
  };

  #start() {
    if (this.#started) return;
    this.#started = true;

    this.#insertCursor();
    this.#place();
    this.#type(this.getAttribute('data-first-text') || '', this.#startDelay);

    this.#after(this.#startDelay + TYPE_DURATION + PAUSE_BETWEEN, () => {
      this.#erase();
      this.#after(ERASE_DURATION, () => this.#type(this.getAttribute('data-last-text') || '', 0));
    });
  }

  /**
   * Put the overlay exactly where the field's own text begins, by MEASURING the input rather than
   * recomposing its offset out of design tokens.
   *
   * 🚨 The token arithmetic was tried first and was wrong by 7.81px at 390, which put the Hebrew
   * text outside the rounded field on the inline-start side — visible at a glance in a crop
   * (review-hd-92/typed-our-1440-800ms.png, the run before this one). The source does not face
   * this: its overlay is `absolute inset-0` inside `.search__field`, the input's own row. Ours
   * cannot copy that, because the only positioned ancestor here is
   * .predictive-search-form__header, and making .predictive-search-form__header-inner relative
   * instead would steal the containing block from .predictive-search__icon, which is absolute and
   * would move (measured: it sits at x=20 from the header, and the inner starts at x=62.22 at 390).
   *
   * So the offset is read off the input each time the drawer opens. RTL is not special-cased by
   * hand: the inline-start edge is derived from `direction`.
   */
  #place() {
    const input = this.#input;
    const wrap = this.parentElement;
    const host = wrap?.offsetParent;
    if (!input || !wrap || !host) return;

    const ib = input.getBoundingClientRect();
    const hb = host.getBoundingClientRect();
    const cs = getComputedStyle(input);
    const padStart = parseFloat(cs.paddingInlineStart) || 0;
    const padEnd = parseFloat(cs.paddingInlineEnd) || 0;
    const rtl = cs.direction === 'rtl';

    // 218.0: ib / hb are DRAWN px, the wrap's insets / sizes and the paddings LAYOUT px - drawn / the wrap's zoom (MDN
    // Element.currentCSSZoom; 1 below 1280) - the hotspot-linea 4ad56cd0 helper. Measured (Home 24 TABLE24 #10): the typed text
    // sat at the top of the field at 1518.
    const z = wrap.currentCSSZoom || 1;
    wrap.style.insetInlineStart = `${(rtl ? hb.right - ib.right : ib.left - hb.left) / z + padStart}px`;
    wrap.style.insetInlineEnd = 'auto';
    wrap.style.insetBlockStart = `${(ib.top - hb.top) / z}px`;
    wrap.style.blockSize = `${ib.height / z}px`;
    wrap.style.maxInlineSize = `${Math.max(0, ib.width / z - padStart - padEnd)}px`;
    wrap.style.fontSize = cs.fontSize;
  }

  /**
   * @param {number} ms
   * @param {() => void} fn
   */
  #after(ms, fn) {
    this.#timers.push(setTimeout(fn, ms));
  }

  /**
   * Reveal by growing the box, not by adding letters — the text is set at once and the element
   * clips it. `prefers-reduced-motion` gets the finished state and no growth.
   *
   * @param {string} text
   * @param {number} delay
   */
  #type(text, delay) {
    this.textContent = text;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.scrollWidth || still) {
      this.style.removeProperty('width');
      this.#cursor?.classList.add('blink');
      return;
    }

    const width = this.scrollWidth;
    this.#run([{ width: '0px' }, { width: `${width}px` }], TYPE_DURATION, delay);
    this.style.width = `${width}px`;
    this.#after(delay + TYPE_DURATION, () => this.#cursor?.classList.add('blink'));
  }

  /**
   * 🚨 One animation at a time, and the previous one is CANCELLED rather than left to finish.
   *
   * The first build gave the erase `fill: 'forwards'`. A filling animation applies at the
   * animation origin of the cascade, which outranks an inline style — so `width: 0` from the erase
   * kept winning after it ended. The second string then animated 0 -> 118 correctly and SNAPPED
   * BACK TO 0 the moment its own animation finished and the erase's fill took over again. The
   * field was left empty for as long as the drawer stayed open, with the real ::placeholder
   * transparent underneath because this overlay is supposed to be supplying it: two invisible
   * things adding up to a blank box. Found by head 55.0 BY EYE (kit-23.9-head55, e159b1f3);
   * every assertion either of us had written passed straight through it, because textContent,
   * visibility, opacity, hidden and the box were all correct and none of them asks whether a
   * glyph was painted.
   *
   * `fill: 'backwards'` only covers the delay before the animation starts, which is what the
   * 0.5s opening delay needs, and leaves the resting width to the inline style.
   *
   * @param {Keyframe[]} frames
   * @param {number} duration
   * @param {number} delay
   */
  #run(frames, duration, delay) {
    this.#anim?.cancel();
    this.#anim = this.animate(frames, { duration, delay, fill: 'backwards', easing: 'linear' });
  }

  #erase() {
    this.#cursor?.classList.remove('blink');
    if (!this.scrollWidth) return;
    this.#run([{ width: `${this.scrollWidth}px` }, { width: '0px' }], ERASE_DURATION, 0);
    this.style.width = '0px';
  }

  #insertCursor() {
    if (this.#cursor) return;
    const cursor = document.createElement('span');
    cursor.className = 'typed-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.textContent = '|';
    this.parentElement?.insertBefore(cursor, this.nextSibling);
    this.#cursor = cursor;
  }
}

if (!customElements.get('search-typed')) {
  customElements.define('search-typed', SearchTyped);
}
